
import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { GripVertical, Trash2 } from "lucide-react";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PrayerTime } from "@/types/page";
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

interface SortablePrayerTimeCardProps {
  prayerTime: PrayerTime;
  onUpdate: (updated: PrayerTime) => void;
  onRemove: (id: string) => void;
}

const SortablePrayerTimeCard: React.FC<SortablePrayerTimeCardProps> = ({ prayerTime, onUpdate, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: prayerTime.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0
  };

  // Ensure time is in valid format
  const isValidTime = (time: any): boolean => {
    if (typeof time !== 'string') {
      return false;
    }
    return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
  };

  const defaultTime = "08:00";
  const [timeValue, setTimeValue] = useState(
    isValidTime(prayerTime.time) ? prayerTime.time : defaultTime
  );

  useEffect(() => {
    // Sync when prayerTime changes from parent
    // Convert any non-string times to strings
    const stringTime = typeof prayerTime.time === 'string' ? prayerTime.time : String(prayerTime.time);
    if (isValidTime(stringTime) && stringTime !== timeValue) {
      setTimeValue(stringTime);
    }
  }, [prayerTime.time]);

  // Check if this is a legacy field
  const isLegacyField = !!(prayerTime as any).legacyFieldName;

  // Handle the removal of this prayer time
  const handleRemove = () => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק זמן תפילה זה?')) {
      console.log("Removing prayer time:", prayerTime.id);
      onRemove(prayerTime.id);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Title changed for prayer time", prayerTime.id, ":", e.target.value);
    onUpdate({ ...prayerTime, title: e.target.value });
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    console.log("Time changed for prayer time", prayerTime.id, ":", newTime);
    setTimeValue(newTime);
    onUpdate({ ...prayerTime, time: newTime });
  };

  return (
    <Card ref={setNodeRef} style={style} className="relative mb-4">
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <button
            className="p-2 hover:bg-gray-100 rounded-md cursor-grab active:cursor-grabbing"
            {...attributes}
            {...listeners}
            type="button"
          >
            <GripVertical className="h-5 w-5 text-gray-400" />
          </button>
          
          <div className="flex-1">
            <div className="flex items-center">
              <Input 
                type="text"
                value={prayerTime.title || ''}
                onChange={handleTitleChange}
                placeholder="שם זמן התפילה"
                className="w-full"
              />
              {isLegacyField && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className="bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-help ml-2">
                        שדה ישן
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>זהו שדה בפורמט ישן - <strong>{(prayerTime as any).legacyFieldName}</strong></p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Input
              type="text"
              value={timeValue}
              onChange={handleTimeChange}
              className="w-32"
              placeholder="00:00"
            />
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleRemove}
              className="h-10 w-10 text-red-500 hover:text-red-700 hover:bg-red-50"
              type="button"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SortablePrayerTimeCard;
